import { useState } from "react";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { Alert, Button, Card, DataTable, SectionCard, type ColumnDef } from "@/deps/ui-library";
import { useOpenInvoices, usePartyDetails, usePartyStanding } from "@/api/queries";
import type { EmailTarget, InvoiceSummary, PartyForm, PartyStanding } from "@/api/types";
import { useCan, useDocumentMailEnabled } from "@/app/session";
import { useToast } from "@/app/toasts";
import { DocumentEmails } from "@/components/DocumentEmails";
import { SendEmailDialog } from "@/components/SendEmailDialog";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { KpiStrip, type Kpi } from "@/components/KpiStrip";
import { PageHeader } from "@/components/PageHeader";
import { PermittedAction } from "@/components/PermittedAction";
import { SegmentControl } from "@/components/SegmentControl";
import { formatDate, today } from "@/lib/dates";
import { formatAmount } from "@/lib/money";
import { termsLabel } from "@/lib/paymentTerms";
import { PartyDialog } from "./PartiesPage";
import { AgeingStrip, DetailRow, LedgerTab } from "./PartyLedger";
import { ageText, limitTone, limitUsed, paisa } from "./standing";
import { useTaxWords } from "@/app/session";

type Tab = "ledger" | "open" | "details";

/**
 * One customer: where they stand, every line between you, and what they still owe.
 *
 * The figures are a report, so someone without `reports.view` still gets the customer's details and
 * open invoices, and doesn't get tiles that could only fail.
 */
export function CustomerDetailPage() {
  const say = useTaxWords();
  const { id } = useParams({ from: "/sales/customers/$id" });
  const search = useSearch({ from: "/sales/customers/$id" });
  const navigate = useNavigate();
  const can = useCan();
  const seesFigures = can("reports.view");
  const asAt = today();

  const { data: party, error } = usePartyDetails(id);
  const { data: standing } = usePartyStanding(id, "customer", asAt, seesFigures);
  const { data: open } = useOpenInvoices(id);
  const [editing, setEditing] = useState(false);
  const [emailing, setEmailing] = useState<EmailTarget | null>(null);
  const mailEnabled = useDocumentMailEnabled();
  const toast = useToast();

  const tab: Tab = search.tab ?? (seesFigures ? "ledger" : "open");
  const name = party ? party.tradingName || party.legalName : "";
  const oldestOverdue = oldestOverdueInvoice(open, asAt);

  if (error) {
    return (
      <Alert
        config={{ variant: "subtle", colorScheme: "danger" }}
        description={(error as Error).message}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Breadcrumbs items={[{ label: "Customers", to: "/sales/customers" }, { label: name }]} />
      <PageHeader
        title={name}
        subtitle={party ? metaLine(party, say) : undefined}
        actions={
          <span className="flex flex-wrap gap-2">
            {mailEnabled && can("sales.invoices.create") ? (
              <>
                <Button
                  colorScheme="default"
                  variant="outline"
                  size="sm"
                  onClick={() => setEmailing({ kind: "statement", id })}
                >
                  Send statement
                </Button>
                <Button
                  colorScheme="default"
                  variant="outline"
                  size="sm"
                  disabled={!oldestOverdue}
                  title={oldestOverdue ? undefined : "Nothing is overdue."}
                  onClick={() =>
                    oldestOverdue && setEmailing({ kind: "reminder", id: oldestOverdue.id })
                  }
                >
                  Send reminder
                </Button>
              </>
            ) : null}
            <PermittedAction
              permission="sales.receipts.create"
              variant="secondary"
              onClick={() => void navigate({ to: "/sales/receipts/new", search: { customer: id } })}
            >
              Record receipt
            </PermittedAction>
            <PermittedAction
              permission="sales.invoices.create"
              onClick={() => void navigate({ to: "/sales/invoices/new", search: { customer: id } })}
            >
              New invoice
            </PermittedAction>
          </span>
        }
      />

      {seesFigures ? (
        <>
          <KpiStrip
            loading={!standing || !party}
            items={standing && party ? tiles(standing, party, open?.length ?? 0) : []}
          />
          {standing ? <AgeingStrip standing={standing} /> : null}
        </>
      ) : null}

      <SegmentControl<Tab>
        label="Customer sections"
        value={tab}
        onChange={(next) =>
          void navigate({
            to: "/sales/customers/$id",
            params: { id },
            search: { ...search, tab: next === "ledger" ? undefined : next },
            replace: true,
          })
        }
        segments={[
          ...(seesFigures ? [{ value: "ledger" as const, label: "Ledger" }] : []),
          { value: "open", label: "Open items", badge: open?.length },
          { value: "details", label: "Details" },
        ]}
      />

      {tab === "ledger" && seesFigures ? (
        <LedgerTab
          id={id}
          kind="customer"
          name={name}
          from={search.from ?? standing?.fiscalYearStart ?? asAt}
          to={search.to ?? asAt}
          setDate={(key, value) =>
            void navigate({
              to: "/sales/customers/$id",
              params: { id },
              search: { ...search, [key]: value },
              replace: true,
            })
          }
        />
      ) : null}
      {tab === "open" ? <OpenItemsTab asAt={asAt} invoices={open} /> : null}
      {tab === "details" && party ? (
        <DetailsTab
          party={party}
          onEdit={can("settings.parties.manage") ? () => setEditing(true) : undefined}
        />
      ) : null}

      <DocumentEmails of={{ kind: "customer", id }} />

      {emailing ? (
        <SendEmailDialog
          target={emailing}
          onClose={() => setEmailing(null)}
          onSent={(recipients) => {
            const what =
              emailing.kind === "statement"
                ? "The statement"
                : `The reminder for ${oldestOverdue?.number}`;
            setEmailing(null);
            toast({
              title: `${what} is on its way to ${recipients.join(", ")}.`,
              scheme: "success",
            });
          }}
        />
      ) : null}

      {editing && party ? (
        <PartyDialog kind="customer" id={id} initial={party} onClose={() => setEditing(false)} />
      ) : null}
    </div>
  );
}

/** The invoice furthest past its due date, or its own date without one, as a reminder is about. */
function oldestOverdueInvoice(
  invoices: InvoiceSummary[] | undefined,
  asAt: string,
): InvoiceSummary | undefined {
  const late = (invoices ?? []).filter(
    (invoice) => paisa(invoice.balance) > 0 && (invoice.due || invoice.date) < asAt,
  );
  return late.reduce<InvoiceSummary | undefined>(
    (oldest, invoice) =>
      !oldest || (invoice.due || invoice.date) < (oldest.due || oldest.date) ? invoice : oldest,
    undefined,
  );
}

function metaLine(party: PartyForm, say: (text: string) => string): string {
  return [
    party.pan ? say(`PAN ${party.pan}`) : "",
    party.phone,
    party.email,
    [party.street, party.city].filter(Boolean).join(", "),
    party.paymentTermsDays === "" ? "" : termsLabel(party.paymentTermsDays),
  ]
    .filter(Boolean)
    .join(" · ");
}

function tiles(standing: PartyStanding, party: PartyForm, openCount: number): Kpi[] {
  const overdue = paisa(standing.overdue) > 0;
  const limit = party.creditLimit.trim();
  const used = limit === "" ? 0 : limitUsed(standing.outstanding, limit);
  const left = (paisa(limit) - paisa(standing.outstanding)) / 100;

  return [
    {
      label: "Outstanding",
      value: formatAmount(standing.outstanding),
      detail: `${openCount} open ${openCount === 1 ? "invoice" : "invoices"}`,
    },
    {
      label: "Overdue",
      value: formatAmount(standing.overdue),
      detail: overdue
        ? `Oldest ${standing.oldestOverdueDays} ${standing.oldestOverdueDays === 1 ? "day" : "days"} past due`
        : "Nothing past due",
      tone: overdue ? "bad" : "default",
    },
    {
      label: "Received this year",
      value: formatAmount(standing.settledThisYear),
      detail: "Fiscal year to date, tax withheld included",
    },
    limit === ""
      ? {
          label: "Credit limit used",
          value: "No limit",
          detail: "Set a limit to warn on new invoices",
        }
      : {
          label: "Credit limit used",
          value: `${used}%`,
          detail:
            left >= 0
              ? `${formatAmount(left)} of ${formatAmount(limit)} available`
              : `${formatAmount(-left)} over a limit of ${formatAmount(limit)}`,
          tone: limitTone(used),
        },
  ];
}

function OpenItemsTab({
  asAt,
  invoices,
}: {
  asAt: string;
  invoices: InvoiceSummary[] | undefined;
}) {
  const columns: Array<ColumnDef<InvoiceSummary>> = [
    {
      id: "document",
      header: "Document",
      cell: ({ row: { original: invoice } }) => (
        <Link
          to="/sales/invoices/$id"
          params={{ id: invoice.id }}
          className="text-primary hover:underline"
        >
          {invoice.number}
        </Link>
      ),
    },
    {
      id: "issued",
      header: "Issued",
      cell: ({ row: { original: invoice } }) => formatDate(invoice.date),
    },
    {
      id: "due",
      header: "Due",
      cell: ({ row: { original: invoice } }) => (invoice.due ? formatDate(invoice.due) : ""),
    },
    {
      id: "amount",
      header: "Amount",
      meta: { textAlign: "right", cellClassName: "num" },
      cell: ({ row: { original: invoice } }) => formatAmount(invoice.total),
    },
    {
      id: "received",
      header: "Received",
      meta: { textAlign: "right", cellClassName: "num" },
      cell: ({ row: { original: invoice } }) =>
        formatAmount((paisa(invoice.total) - paisa(invoice.balance)) / 100),
    },
    {
      id: "open",
      header: "Open",
      meta: { textAlign: "right", cellClassName: "num font-medium" },
      cell: ({ row: { original: invoice } }) => formatAmount(invoice.balance),
    },
    {
      id: "ageing",
      header: "Ageing",
      cell: ({ row: { original: invoice } }) => ageText(asAt, invoice.date, invoice.due),
    },
  ];
  const total = (invoices ?? []).reduce((sum, invoice) => sum + paisa(invoice.balance), 0);

  return (
    <div className="flex flex-col gap-2">
      <Card size="none" className="overflow-hidden">
        <DataTable
          aria-label="Open items"
          size="sm"
          enableSorting={false}
          columns={columns}
          data={invoices ?? []}
          getRowId={(invoice) => invoice.id}
          loading={invoices === undefined}
          emptyContent="Nothing is open for this customer."
          pagination={{ mode: "manual", show: false }}
        />
      </Card>
      <p className="text-fg-muted text-xs">
        {invoices?.length ?? 0} open · {formatAmount(total / 100)} outstanding
      </p>
    </div>
  );
}

function DetailsTab({ party, onEdit }: { party: PartyForm; onEdit: (() => void) | undefined }) {
  const say = useTaxWords();
  return (
    <div className="flex flex-col gap-3">
      {onEdit ? (
        <div>
          <Button colorScheme="default" variant="outline" size="sm" onClick={onEdit}>
            Edit customer
          </Button>
        </div>
      ) : null}
      <div className="grid gap-3 md:grid-cols-2">
        <SectionCard title="Identity">
          <dl>
            <DetailRow label="Legal name" value={party.legalName} />
            <DetailRow label="Trading name" value={party.tradingName} />
            <DetailRow label={say("PAN")} value={party.pan} />
            <DetailRow
              label={say("Registered for VAT")}
              value={party.vatRegistered ? "Yes" : "No"}
            />
          </dl>
        </SectionCard>
        <SectionCard title="Contact">
          <dl>
            <DetailRow label="Phone" value={party.phone} />
            <DetailRow label="Email" value={party.email} />
          </dl>
        </SectionCard>
        <SectionCard title="Address">
          <dl>
            <DetailRow label="Street" value={party.street} />
            <DetailRow label="City" value={party.city} />
          </dl>
        </SectionCard>
        <SectionCard title="Billing and accounting">
          <dl>
            <DetailRow label="Payment terms" value={termsLabel(party.paymentTermsDays)} />
            <DetailRow
              label="Credit limit"
              value={party.creditLimit.trim() === "" ? "No limit" : formatAmount(party.creditLimit)}
            />
          </dl>
        </SectionCard>
      </div>
    </div>
  );
}
